import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseInfiniteQueryResult,
  type UseMutationResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import type {MutationConfig} from '../../lib/react-query';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  AdviserQuestionsResponse,
  AdviserResponseSchema,
  AdvisersResponse,
  AuthUrlResponse,
  BoxTagsResponseSchema,
  DeleteLikeResponse,
  OpenQuestionsResponse,
  PostAnswerResponse,
  PostLikeResponse,
  PostQuestionNonDisplayResponse,
  PostQuestionResponse,
  PutTwitterTokenResponse,
  QuestionAnswersResponse,
  QuestionResponse,
  QuestionsResponse,
} from '../entities/box/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';
import type {
  AdviserQuestionsResponseType,
  AdviserResponse,
  AdvisersResponseType,
  AuthUrlResponseType,
  BoxTagsResponseType,
  DeleteLikeArgs,
  DeleteLikeResponseType,
  GetAdviserArgs,
  GetAdviserQuestionsArgs,
  GetAdvisersArgs,
  GetAnswerArgs,
  GetOpenQuestionsArgs,
  GetQuestionArgs,
  GetQuestionsArgs,
  GetTagsArgs,
  OpenQuestionsResponseType,
  PostAdviserProfileDTOType,
  PostAnswerArgs,
  PostAnswerDTOType,
  PostAnswerResponseType,
  PostLikeArgs,
  PostLikeNonDisplayArgs,
  PostLikeResponseType,
  PostQuestionArgs,
  PostQuestionDTOType,
  PostQuestionNonDisplayResponseType,
  PostQuestionResponseType,
  PutAdviserArgs,
  PutTwitterTokenResponseType,
  QuestionAnswersResponseType,
  QuestionResponseType,
  QuestionsResponseType,
  UseDeleteLikeOptions,
  UseGetAdviserOptions,
  UseGetAdviserQuestionsOptions,
  UseGetAdvisersOptions,
  UseGetAnswersOptions,
  UseGetOpenQuestionsOptions,
  UseGetQuestionOptions,
  UseGetQuestionsOptions,
  UseGetTagsOptions,
  getAuthUrlArgs,
  putTwitterTokenArgs,
} from './boxRepositoryTypes';

/**
 * GetAdviserTags
 * GET /adviser-tags
 */

export const getTags = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetTagsArgs): Promise<
  ResultWithOauthToken<BoxTagsResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<BoxTagsResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    return defaultClient.get(`/${version}/adviser-tags`, BoxTagsResponseSchema);
  };

  const result = await sendApiRequest<BoxTagsResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

export const useTags = ({
  config,
  version = DEFAULT_API_VERSION,
}: UseGetTagsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<BoxTagsResponseType, AppError>>({
    ...config,
    async queryFn() {
      const cookies = parseCookies();

      const result = await getTags({cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * PostLike
 * POST /advisers/{adviserName}/questions/{questionId}/like
 */

export const postLike = async ({
  cookies,
  questionId,
  adviserName,
  version = DEFAULT_API_VERSION,
}: PostLikeArgs): Promise<
  ResultWithOauthToken<PostLikeResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<PostLikeResponseType, AppError>> => {
    defaultClient.setToken(token);
    return defaultClient.post(
      `/${version}/advisers/${adviserName}/questions/${questionId}/like`,
      PostLikeResponse,
      {},
    );
  };

  const result = await sendApiRequest<PostLikeResponseType>({
    cookies,
    authRequired: false, // 未ログインの場合はいいねカウントのみ更新するため
    request,
    version,
  });

  return result;
};

// PostLikeを型として使用していたため、移動できず
type UsePostLikeOptions = {
  config?: MutationConfig<typeof postLike>;
  version?: string;
  onSuccessFunc?: () => void;
  onErrorFunc?: () => void;
};

type MutationFnArgs = {
  questionId: number;
  adviserName: string;
};

// どこからも参照されていない 2025/04/24
export const usePostLike = ({
  version = DEFAULT_API_VERSION,
  onSuccessFunc,
  onErrorFunc,
}: UsePostLikeOptions): UseMutationResult<
  ResultWithOauthToken<PostLikeResponseType, AppError>,
  AppError,
  MutationFnArgs
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async ({questionId, adviserName}) => {
      const cookies = parseCookies();
      const result = await postLike({
        cookies,
        questionId,
        adviserName,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        if (onSuccessFunc) {
          onSuccessFunc();
        }
      },
      onError() {
        if (onErrorFunc) {
          onErrorFunc();
        }
      },
    },
  );
};

/**
 * DeleteLike
 * DELETE /advisers/{adviserName}/questions/{questionId}/like
 */

export const deleteLike = async ({
  cookies,
  questionId,
  adviserName,
  version = DEFAULT_API_VERSION,
}: DeleteLikeArgs): Promise<
  ResultWithOauthToken<DeleteLikeResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<DeleteLikeResponseType, AppError>> => {
    defaultClient.setToken(token);
    return defaultClient.delete(
      `/${version}/advisers/${adviserName}/questions/${questionId}/like`,
      DeleteLikeResponse,
    );
  };

  const result = await sendApiRequest<DeleteLikeResponseType>({
    cookies,
    authRequired: false, // 未ログインの場合はいいねカウントのみ更新するため
    request,
    version,
  });

  return result;
};

type UseDeleteLikeMutationArgs = {
  questionId: number;
  adviserName: string;
};

export const useDeleteLike = ({
  config,
  version = DEFAULT_API_VERSION,
  onSuccessFunc,
  onErrorFunc,
}: UseDeleteLikeOptions): UseMutationResult<
  ResultWithOauthToken<DeleteLikeResponseType, AppError>,
  AppError,
  UseDeleteLikeMutationArgs
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async ({questionId, adviserName}) => {
      const cookies = parseCookies();

      const result = await deleteLike({
        cookies,
        questionId,
        adviserName,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        if (onSuccessFunc) {
          onSuccessFunc();
        }
      },
      onError() {
        if (onErrorFunc) {
          onErrorFunc();
        }
      },
      ...config,
    },
  );
};

/**
 * GetAdviser
 * GET /advisers/{adviserName}
 */

export const getAdviser = async ({
  cookies,
  adviserName,
  version = DEFAULT_API_VERSION,
}: GetAdviserArgs): Promise<
  ResultWithOauthToken<AdviserResponse, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<AdviserResponse, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    return defaultClient.get(
      `/${version}/advisers/${adviserName}`,
      AdviserResponseSchema,
    );
  };

  const result = await sendApiRequest<AdviserResponse>({
    cookies,
    authRequired: false,
    request,
    version,
    allowReturnError: true,
  });

  return result;
};

export const useAdviser = ({
  config,
  adviserName,
  version = DEFAULT_API_VERSION,
}: UseGetAdviserOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<AdviserResponse, AppError>>({
    ...config,
    queryKey: ['adviser', adviserName],
    async queryFn() {
      const cookies = parseCookies();

      const result = await getAdviser({cookies, adviserName, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * PutAdviser
 * PUT /advisers/{adviserName}
 */

type UsePutAdviserOptions = {
  config?: MutationConfig<typeof putAdviser>;
  version?: string;
  onSuccessFunc?: () => void;
  onErrorFunc?: () => void;
};

export const putAdviser = async ({
  cookies,
  adviserName,
  data,
  version = DEFAULT_API_VERSION,
}: PutAdviserArgs): Promise<
  ResultWithOauthToken<AdviserResponse, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<AdviserResponse, AppError>> => {
    defaultClient.setToken(token);
    return defaultClient.put(
      `/${version}/advisers/${adviserName}`,
      AdviserResponseSchema,
      data,
    );
  };

  const result = await sendApiRequest<AdviserResponse>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

type UsePutAdviserMutationArgs = {
  adviserName: string;
  data: PostAdviserProfileDTOType;
};

export const usePutAdviser = ({
  config,
  version = DEFAULT_API_VERSION,
  onSuccessFunc,
  onErrorFunc,
}: UsePutAdviserOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation(
    async ({adviserName, data}: UsePutAdviserMutationArgs) => {
      const cookies = parseCookies();
      const result = await putAdviser({cookies, adviserName, data, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        if (onSuccessFunc) {
          onSuccessFunc();
        }
      },
      onError() {
        if (onErrorFunc) {
          onErrorFunc();
        }
      },
      ...config,
    },
  );
};

/**
 * GetAdvisers
 * GET /advisers
 */

export const getAdvisers = async ({
  cookies,
  page = 1,
  limit = 20,
  hashtagIDs = [],
  version = DEFAULT_API_VERSION,
}: GetAdvisersArgs): Promise<
  ResultWithOauthToken<AdvisersResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<AdvisersResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    const path = createPathWithQuery(`/${version}/advisers`, {
      page,
      limit,
      hashtagIDs,
    });
    return defaultClient.get(path, AdvisersResponse);
  };

  const result = await sendApiRequest<AdvisersResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

export const useAdvisers = ({
  config,
  page = 1,
  limit = 20,
  hashtagIDs = [],
  version = DEFAULT_API_VERSION,
}: UseGetAdvisersOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultWithOauthToken<AdvisersResponseType, AppError>>({
    ...config,
    async queryFn() {
      const cookies = parseCookies();

      const result = await getAdvisers({
        cookies,
        page,
        limit,
        hashtagIDs,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useInfiniteAdvisers = ({
  limit = 20,
  hashtagIDs = [],
  version = DEFAULT_API_VERSION,
}: UseGetAdvisersOptions): UseInfiniteQueryResult<
  ResultWithOauthToken<AdvisersResponseType, AppError>,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['advisers', hashtagIDs],
    async ({pageParam: pageParameter = 1}) => {
      const cookies = parseCookies();

      const result = await getAdvisers({
        cookies,
        page: pageParameter,
        limit,
        hashtagIDs,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.ok ? firstPage.value.nextURLParam.page : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextURLParam.page > 0
          ? lastPage.value.nextURLParam.page
          : undefined,
    },
  );
};

/**
 * GetAdviserQuestions
 * GET /advisers/{adviserName}/questions
 */

export const getAdviserQuestions = async ({
  cookies,
  adviserName,
  page = 1,
  limit = 20,
  hasAnswer = 1,
  questionOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: GetAdviserQuestionsArgs): Promise<
  ResultWithOauthToken<AdviserQuestionsResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<AdviserQuestionsResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    const path = createPathWithQuery(
      `/${version}/advisers/${adviserName}/questions`,
      {
        page,
        limit,
        hasAnswer,
        questionOrderType,
      },
    );
    return defaultClient.get(path, AdviserQuestionsResponse);
  };

  const result = await sendApiRequest<AdviserQuestionsResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

export const useAdviserQuestions = ({
  config,
  adviserName,
  page = 1,
  limit = 20,
  hasAnswer = 1,
  questionOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: UseGetAdviserQuestionsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<AdviserQuestionsResponseType, AppError>>(
    {
      ...config,
      queryKey: ['adviserQuestions', adviserName, hasAnswer, questionOrderType],
      async queryFn() {
        const cookies = parseCookies();

        const result = await getAdviserQuestions({
          cookies,
          adviserName,
          page,
          limit,
          hasAnswer,
          questionOrderType,
          version,
        });
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );
};

export const useInfiniteAdviserQuestions = ({
  adviserName,
  limit = 20,
  hasAnswer = 1,
  questionOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: UseGetAdviserQuestionsOptions): UseInfiniteQueryResult<
  ResultWithOauthToken<AdviserQuestionsResponseType, AppError>,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['advisers', hasAnswer, questionOrderType],
    async ({pageParam: pageParameter = 1}) => {
      const cookies = parseCookies();

      const result = await getAdviserQuestions({
        cookies,
        adviserName,
        page: pageParameter,
        limit,
        hasAnswer,
        questionOrderType,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.ok ? firstPage.value.nextURLParam.page : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextURLParam.page > 0
          ? lastPage.value.nextURLParam.page
          : undefined,
    },
  );
};

/**
 * GetQuestion
 * GET /questions/{questionId}
 */

export const getQuestion = async ({
  cookies,
  questionId,
  version = DEFAULT_API_VERSION,
}: GetQuestionArgs): Promise<
  ResultWithOauthToken<QuestionResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<QuestionResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    return defaultClient.get(
      `/${version}/questions/${questionId}`,
      QuestionResponse,
    );
  };

  const result = await sendApiRequest<QuestionResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
    allowReturnError: true,
  });

  return result;
};

export const useQuestion = ({
  questionId,
  config,
  version,
}: UseGetQuestionOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<QuestionResponseType, AppError>>({
    ...config,
    async queryFn() {
      const cookies = parseCookies();

      const result = await getQuestion({cookies, questionId, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * PostQuestion
 * POST /questions
 */

// typeof postQuestionのために移動できず
type UsePostQuestionOptions = {
  config?: MutationConfig<typeof postQuestion>;
  data: PostQuestionDTOType;
  version?: string;
};

export const postQuestion = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PostQuestionArgs): Promise<
  ResultWithOauthToken<PostQuestionResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<PostQuestionResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    return defaultClient.post(
      `/${version}/questions`,
      PostQuestionResponse,
      data,
    );
  };

  const result = await sendApiRequest<PostQuestionResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

export const usePostQuestion = ({
  data,
  version,
}: UsePostQuestionOptions): UseMutationResult<
  ResultWithOauthToken<PostQuestionResponseType, AppError>,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation(async () => {
    const cookies = parseCookies();

    const result = await postQuestion({cookies, data, version});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  }, {});
};

/**
 * PostQuestionNonDisplay
 * POST /advisers/${adviserName}/questions/${questionId}/non-display
 */

// typeof postQuestionNonDisplayのために移動できず
type UsePostLikeNonDisplayOptions = {
  config?: MutationConfig<typeof postQuestionNonDisplay>;
  questionId: number;
  adviserName: string;
  version?: string;
};

export const postQuestionNonDisplay = async ({
  cookies,
  questionId,
  adviserName,
  version = DEFAULT_API_VERSION,
}: PostLikeNonDisplayArgs): Promise<
  ResultWithOauthToken<PostQuestionNonDisplayResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<PostQuestionNonDisplayResponseType, AppError>> => {
    defaultClient.setToken(token);
    return defaultClient.post(
      `/${version}/advisers/${adviserName}/questions/${questionId}/non-display`,
      PostQuestionNonDisplayResponse,
      {},
    );
  };

  const result = await sendApiRequest<PostQuestionNonDisplayResponseType>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const usePostQuestionNonDisplay = ({
  questionId,
  adviserName,
  version = DEFAULT_API_VERSION,
}: UsePostLikeNonDisplayOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    ResultWithOauthToken<PostQuestionNonDisplayResponseType, AppError>
  >(async () => {
    const cookies = parseCookies();

    const result = await postQuestionNonDisplay({
      cookies,
      questionId,
      adviserName,
      version,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

/**
 * GetQuestionAnswers
 * GET /questions/{questionId}/answers
 */

export const getAnswers = async ({
  cookies,
  questionId,
  page = 1,
  limit = 10,
  AnswerOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: GetAnswerArgs): Promise<
  ResultWithOauthToken<QuestionAnswersResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<QuestionAnswersResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    const path = createPathWithQuery(
      `/${version}/questions/${questionId}/answers`,
      {
        page,
        limit,
        AnswerOrderType,
      },
    );
    return defaultClient.get(path, QuestionAnswersResponse);
  };

  const result = await sendApiRequest<QuestionAnswersResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
    allowReturnError: true,
  });

  return result;
};

export const useAnswers = ({
  config,
  questionId,
  page = 1,
  limit = 10,
  AnswerOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: UseGetAnswersOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<QuestionAnswersResponseType, AppError>>({
    ...config,
    queryKey: ['question', questionId, AnswerOrderType],
    async queryFn() {
      const cookies = parseCookies();

      const result = await getAnswers({
        cookies,
        questionId,
        page,
        limit,
        AnswerOrderType,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useInfiniteAnswers = ({
  questionId,
  limit = 10,
  AnswerOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: UseGetAnswersOptions): UseInfiniteQueryResult<
  ResultWithOauthToken<QuestionAnswersResponseType, AppError>,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['question', questionId, AnswerOrderType],
    async ({pageParam: pageParameter = 1}) => {
      const cookies = parseCookies();

      const result = await getAnswers({
        cookies,
        questionId,
        page: pageParameter,
        limit,
        AnswerOrderType,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.ok ? firstPage.value.nextURLParam.page : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextURLParam.page > 0
          ? lastPage.value.nextURLParam.page
          : undefined,
    },
  );
};

/**
 * PostAnswer
 * POST /questions/{questionId}/answers
 */

// typeof postAnswerのために移動できず
type UsePostAnswerOptions = {
  config?: MutationConfig<typeof postAnswer>;
  questionId: number;
  data: PostAnswerDTOType;
  version?: string;
};

export const postAnswer = async ({
  cookies,
  questionId,
  data,
  version = DEFAULT_API_VERSION,
}: PostAnswerArgs): Promise<
  ResultWithOauthToken<PostAnswerResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<PostAnswerResponseType, AppError>> => {
    defaultClient.setToken(token);
    return defaultClient.post(
      `/${version}/questions/${questionId}/answers`,
      PostAnswerResponse,
      data,
    );
  };

  const result = await sendApiRequest<PostAnswerResponseType>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const usePostAnswer = ({
  questionId,
  data,
  version = DEFAULT_API_VERSION,
}: UsePostAnswerOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<ResultWithOauthToken<PostAnswerResponseType, AppError>>(
    async () => {
      const cookies = parseCookies();

      const result = await postAnswer({cookies, questionId, data, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  );
};

/**
 * GetQuestions
 * GET /questions
 */

export const getQuestions = async ({
  cookies,
  q = '',
  page = 1,
  limit = 20,
  questionOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: GetQuestionsArgs): Promise<
  ResultWithOauthToken<QuestionsResponseType, AppError>
> => {
  const request = async (
    token?: string,
  ): Promise<Result<QuestionsResponseType, AppError>> => {
    if (token) {
      defaultClient.setToken(token);
    }

    const path = createPathWithQuery(`/${version}/questions`, {
      q,
      page,
      limit,
      questionOrderType,
    });
    return defaultClient.get(path, QuestionsResponse);
  };

  const result = await sendApiRequest<QuestionsResponseType>({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

export const useQuestions = ({
  config,
  q = '',
  page = 1,
  limit = 20,
  questionOrderType = 'AnsweredAt',
  version,
}: UseGetQuestionsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<QuestionsResponseType, AppError>>({
    ...config,
    async queryFn() {
      const cookies = parseCookies();

      const result = await getQuestions({
        cookies,
        q,
        page,
        limit,
        questionOrderType,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useInfiniteQuestions = ({
  q = '',
  limit = 20,
  questionOrderType = 'AnsweredAt',
  version = DEFAULT_API_VERSION,
}: UseGetQuestionsOptions): UseInfiniteQueryResult<
  ResultWithOauthToken<QuestionsResponseType, AppError>,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['questions', q, questionOrderType],
    async ({pageParam: pageParameter = 1}) => {
      const cookies = parseCookies();

      const result = await getQuestions({
        cookies,
        q,
        page: pageParameter,
        limit,
        questionOrderType,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.ok ? firstPage.value.nextURLParam.page : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextURLParam.page > 0
          ? lastPage.value.nextURLParam.page
          : undefined,
    },
  );
};

/**
 * GetOpenQuestions
 * GET /open-questions
 */

export const getOpenQuestions = async ({
  cookies,
  page = 1,
  limit = 20,
  version = DEFAULT_API_VERSION,
}: GetOpenQuestionsArgs): Promise<
  ResultWithOauthToken<OpenQuestionsResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<OpenQuestionsResponseType, AppError>> => {
    defaultClient.setToken(token);
    const path = createPathWithQuery(`/${version}/open-questions`, {
      page,
      limit,
    });
    return defaultClient.get(path, OpenQuestionsResponse);
  };

  const result = await sendApiRequest<OpenQuestionsResponseType>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const useOpenQuestions = ({
  config,
  page = 1,
  limit = 20,
  version = DEFAULT_API_VERSION,
}: UseGetOpenQuestionsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultWithOauthToken<OpenQuestionsResponseType, AppError>>({
    ...config,
    queryKey: ['openQuestion'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getOpenQuestions({
        cookies,
        page,
        limit,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useInfiniteOpenQuestions = ({
  limit = 20,
  version = DEFAULT_API_VERSION,
}: UseGetOpenQuestionsOptions): UseInfiniteQueryResult<
  ResultWithOauthToken<OpenQuestionsResponseType, AppError>,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['question'],
    async ({pageParam: pageParameter = 1}) => {
      const cookies = parseCookies();
      const result = await getOpenQuestions({
        cookies,
        page: pageParameter,
        limit,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.ok ? firstPage.value.nextURLParam.page : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextURLParam.page > 0
          ? lastPage.value.nextURLParam.page
          : undefined,
    },
  );
};

/**
 * PutAdviserToken
 * PUT /advisers/token
 */

export const putTwitterToken = async ({
  cookies,
  requestToken,
  verifier,
  version = DEFAULT_API_VERSION,
}: putTwitterTokenArgs): Promise<
  ResultWithOauthToken<PutTwitterTokenResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<PutTwitterTokenResponseType, AppError>> => {
    defaultClient.setToken(token);
    const data = {
      requestToken,
      verifier,
    };
    return defaultClient.put(
      `/${version}/advisers/token`,
      PutTwitterTokenResponse,
      data,
    );
  };

  const result = await sendApiRequest<PutTwitterTokenResponseType>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

/**
 * GetAuthUrl
 * /configurations/oauth/twitter
 */

export const getAuthUrl = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: getAuthUrlArgs): Promise<
  ResultWithOauthToken<AuthUrlResponseType, AppError>
> => {
  const request = async (
    token: string,
  ): Promise<Result<AuthUrlResponseType, AppError>> => {
    defaultClient.setToken(token);
    const purpose = 'updateToken';
    const path = createPathWithQuery(
      `/${version}/configurations/oauth/twitter`,
      {
        purpose,
      },
    );
    return defaultClient.get(path, AuthUrlResponse);
  };

  const result = await sendApiRequest<AuthUrlResponseType>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};
